"use client";

import React, { useState, useEffect } from "react";
import { useWorkspace } from "@/lib/context/WorkspaceContext";

interface InterestFormModalProps {
    clickedCommunity: string;
    pipelineKey?: string;
    children?: React.ReactNode;
}

export function InterestFormModal({ clickedCommunity, pipelineKey, children }: InterestFormModalProps) {
    const { user } = useWorkspace();
    const [isOpen, setIsOpen] = useState(false);
    const [step, setStep] = useState<"banner" | "form" | "success">("form");
    
    const userHomeCommunityRaw = user?.community || user?.assignments?.find(a => a.entity_type === "community")?.title || null;
    
    // Normalize names to match "New Delhi Community" with "New Delhi"
    const normalizeName = (name: string | null) => {
        if (!name) return "";
        return name.toLowerCase().replace(/\s+(community|chapter)$/i, "").trim();
    };
    
    const userHomeCommunity = userHomeCommunityRaw ? normalizeName(userHomeCommunityRaw) : null;
    const normalizedClicked = normalizeName(clickedCommunity);
    
    const [targetCommunity, setTargetCommunity] = useState(clickedCommunity);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [selectedOptions, setSelectedOptions] = useState<string[]>([]);

    const options = [
        "Event Planning",
        "Networking & Mentorship",
        "Content Creation",
        "Chapter Leadership",
        "Sponsorship & Partnerships",
        "General Volunteer"
    ];

    useEffect(() => {
        if (isOpen) {
            setSelectedOptions([]);
            setIsSubmitting(false);
            
            if (
                userHomeCommunity && 
                userHomeCommunity !== "unassigned" &&
                userHomeCommunity !== normalizedClicked && 
                userHomeCommunity !== pipelineKey?.toLowerCase().trim()
            ) {
                setStep("banner");
            } else {
                setTargetCommunity(clickedCommunity);
                setStep("form");
            }
        }
    }, [isOpen, clickedCommunity, userHomeCommunity, normalizedClicked, pipelineKey]);

    const handleOptionToggle = (option: string) => {
        setSelectedOptions(prev => 
            prev.includes(option) ? prev.filter(o => o !== option) : [...prev, option]
        );
    };

    const handleProceedBanner = (proceedWith: string) => {
        setTargetCommunity(proceedWith);
        setStep("form");
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            const { fetchWithAuth } = await import("@/lib/auth");
            const res = await fetchWithAuth("/api/v1/interest", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    target_community: targetCommunity,
                    selected_options: selectedOptions
                })
            });

            if (!res.ok) {
                throw new Error("Failed to submit interest");
            }

            setStep("success");
        } catch (error) {
            console.error("Error submitting interest:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <div onClick={() => setIsOpen(true)} className="inline-block cursor-pointer">
                {children || (
                    <button className="bg-[#007a8a] text-white px-4 py-2 rounded font-medium hover:bg-[#00606d] transition-colors">
                        Express interest in joining
                    </button>
                )}
            </div>

            {isOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                        {step === "banner" && (
                            <div className="p-6">
                                <h2 className="text-xl font-bold text-[#003d45] mb-2">Wait, are you in {clickedCommunity}?</h2>
                                <p className="text-[#666666] mb-6">
                                    We noticed your home community seems to be {userHomeCommunityRaw}. 
                                    Would you like to reroute to the {userHomeCommunityRaw} form, or proceed with the current {clickedCommunity} form?
                                </p>
                                <div className="flex flex-col space-y-3 sm:flex-row sm:space-x-3 sm:space-y-0">
                                    <button 
                                        onClick={() => handleProceedBanner(userHomeCommunityRaw!)}
                                        className="flex-1 border border-[#003d45] text-[#003d45] px-4 py-2 rounded font-medium hover:bg-gray-50 transition-colors"
                                    >
                                        Reroute to {userHomeCommunityRaw}
                                    </button>
                                    <button 
                                        onClick={() => handleProceedBanner(clickedCommunity)}
                                        className="flex-1 bg-[#007a8a] text-white px-4 py-2 rounded font-medium hover:bg-[#00606d] transition-colors"
                                    >
                                        Proceed with {clickedCommunity}
                                    </button>
                                </div>
                            </div>
                        )}

                        {step === "form" && (
                            <div className="p-6">
                                <h2 className="text-xl font-bold text-[#003d45] mb-2">Join {targetCommunity}</h2>
                                <p className="text-[#666666] mb-6">
                                    Let us know how you'd like to get involved. Select all that apply.
                                </p>
                                
                                <div className="space-y-3 mb-6">
                                    {options.map((option) => (
                                        <label key={option} className="flex items-center space-x-3 cursor-pointer p-2 hover:bg-gray-50 rounded">
                                            <input 
                                                type="checkbox"
                                                checked={selectedOptions.includes(option)}
                                                onChange={() => handleOptionToggle(option)}
                                                className="w-5 h-5 rounded border-gray-300 text-[#007a8a] focus:ring-[#007a8a]"
                                            />
                                            <span className="text-[#1a1a1a]">{option}</span>
                                        </label>
                                    ))}
                                </div>

                                <div className="flex justify-end space-x-3">
                                    <button 
                                        onClick={() => setIsOpen(false)}
                                        disabled={isSubmitting}
                                        className="px-4 py-2 rounded text-[#666666] hover:bg-gray-100 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        onClick={handleSubmit} 
                                        disabled={isSubmitting || selectedOptions.length === 0}
                                        className="bg-[#007a8a] text-white px-4 py-2 rounded font-medium hover:bg-[#00606d] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {isSubmitting ? "Submitting..." : "Submit Interest"}
                                    </button>
                                </div>
                            </div>
                        )}

                        {step === "success" && (
                            <div className="p-6 text-center">
                                <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                                </div>
                                <h2 className="text-xl font-bold text-[#003d45] mb-2">Thank You!</h2>
                                <p className="text-[#666666] mb-6">
                                    Your interest in the {targetCommunity} community has been recorded. Our team will reach out to you soon.
                                </p>
                                <button 
                                    onClick={() => setIsOpen(false)} 
                                    className="w-full bg-[#007a8a] text-white px-4 py-2 rounded font-medium hover:bg-[#00606d] transition-colors"
                                >
                                    Close
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
